import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://eventshowcase.de';

const ATTENDEE_STEPS = [
  { icon: 'ri-search-line', title: 'Discover Events', desc: 'Browse thousands of events by category, location, or date. Filter by free events, this weekend, or your favourite genres.' },
  { icon: 'ri-ticket-line', title: 'Register & Pay', desc: 'Secure your spot in seconds. Pay with card, bank transfer, or mobile money. Get your ticket instantly via email.' },
  { icon: 'ri-calendar-check-line', title: 'Attend & Enjoy', desc: 'Show your QR code at the door. Follow the organizer to stay updated on future events.' },
];

const ORGANIZER_STEPS = [
  { icon: 'ri-user-add-line', title: 'Create Your Account', desc: 'Sign up free in under a minute. No credit card required to get started on the Starter plan.' },
  { icon: 'ri-calendar-event-line', title: 'Create Your Event', desc: 'Fill in your event details, set your ticket price, upload a cover image, and publish. It takes less than 5 minutes.' },
  { icon: 'ri-share-line', title: 'Promote & Grow', desc: 'Share your event link. Use the social follow gate to grow your audience — attendees follow your social accounts to register.' },
  { icon: 'ri-money-dollar-circle-line', title: 'Get Paid', desc: 'Revenue is held for 48 hours after your event ends, then released to your wallet. Withdraw anytime via bank or mobile money.' },
];

const FAQS = [
  { q: 'Is it free to create an account?', a: 'Yes! Creating an account and listing events is free on the Starter plan. We only charge a 10% commission on paid ticket sales.' },
  { q: 'How does the social follow gate work?', a: 'You can require attendees to follow your social media accounts (Instagram, TikTok, etc.) before completing registration. This helps you grow your audience organically.' },
  { q: 'When do I receive my earnings?', a: 'Event earnings are held for 48 hours after your event ends to allow for any disputes. Store product earnings are held for 7 days. After that, funds are available to withdraw.' },
  { q: 'What payment methods are supported?', a: 'We support card payments, bank transfers, and mobile money via Paystack and Flutterwave — covering most African and global markets.' },
  { q: 'Can I host online events?', a: 'Absolutely. When creating your event, select "Online" as the location type and add your streaming link.' },
  { q: 'How do I upgrade my plan?', a: 'Go to Dashboard → Subscription. Growth ($19/mo) cuts commission to 5%, Pro Partner ($39/mo) drops it to just 2%.' },
];

function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-gray-50 transition-colors">
        <span className="text-sm font-semibold text-gray-900">{question}</span>
        <i className={`ri-arrow-down-s-line text-gray-400 text-lg transition-transform ${open ? 'rotate-180' : ''}`}></i>
      </button>
      {open && <div className="px-5 pb-4"><p className="text-sm text-gray-600 leading-relaxed">{answer}</p></div>}
    </div>
  );
}

export default function HowItWorksPage() {
  const { user, profile } = useAuth();
  const [eventsCreated, setEventsCreated] = useState(0);
  const [eventsAttended, setEventsAttended] = useState(0);

  const firstName = profile?.full_name?.split(' ')[0] || user?.user_metadata?.full_name?.split(' ')[0] || null;

  useEffect(() => {
    const PAGE_TITLE = 'How It Works – Create & Discover Events | EVENTSHOWCASE';
    const PAGE_DESC = 'Learn how EVENTSHOWCASE works for attendees and organizers. Discover events, register in seconds, or create and monetize your own events in minutes.';
    const PAGE_URL = `${SITE_URL}/how-it-works`;
    const PAGE_IMG = 'https://public.readdy.ai/ai/img_res/0b544f3511a676f4e21083671e2809b9.jpg';

    document.title = PAGE_TITLE;

    const setMeta = (sel: string, attr: string, val: string) => {
      let el = document.querySelector(sel) as HTMLMetaElement | null;
      if (!el) { el = document.createElement('meta'); const [a, v] = sel.replace('meta[', '').replace(']', '').split('='); el.setAttribute(a.trim(), v.replace(/"/g, '').trim()); document.head.appendChild(el); el.setAttribute('data-page-meta', 'true'); }
      el.setAttribute(attr, val);
    };
    setMeta('meta[name="description"]', 'content', PAGE_DESC);
    setMeta('meta[property="og:title"]', 'content', PAGE_TITLE);
    setMeta('meta[property="og:description"]', 'content', PAGE_DESC);
    setMeta('meta[property="og:url"]', 'content', PAGE_URL);
    setMeta('meta[property="og:type"]', 'content', 'website');
    setMeta('meta[property="og:image"]', 'content', PAGE_IMG);
    setMeta('meta[property="og:image:width"]', 'content', '1200');
    setMeta('meta[property="og:image:height"]', 'content', '630');
    setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'content', PAGE_TITLE);
    setMeta('meta[name="twitter:description"]', 'content', PAGE_DESC);
    setMeta('meta[name="twitter:image"]', 'content', PAGE_IMG);

    // Schema.org JSON-LD
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: 'How EVENTSHOWCASE Works',
      description: 'Learn how to discover, attend, and create events on EVENTSHOWCASE. Step-by-step guide for attendees and organizers.',
      totalTime: 'PT5M',
      estimatedCost: {
        '@type': 'MonetaryAmount',
        currency: 'USD',
        value: '0',
      },
      step: [
        ...ATTENDEE_STEPS.map((step, i) => ({
          '@type': 'HowToStep',
          position: i + 1,
          name: step.title,
          text: step.desc,
        })),
        ...ORGANIZER_STEPS.map((step, i) => ({
          '@type': 'HowToStep',
          position: i + 4,
          name: step.title,
          text: step.desc,
        })),
      ],
    };

    const existing = document.getElementById('schema-how-it-works');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = 'schema-how-it-works';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('schema-how-it-works');
      if (el) el.remove();
      document.querySelectorAll('meta[data-page-meta="true"]').forEach(e => e.remove());
    };
  }, []);

  useEffect(() => {
    if (user) fetchUserStats();
  }, [user]);

  const fetchUserStats = async () => {
    if (!user) return;
    const [{ count: created }, { count: attended }] = await Promise.all([
      supabase.from('events').select('id', { count: 'exact', head: true }).eq('organizer_id', user.id),
      supabase.from('registrations').select('id', { count: 'exact', head: true }).eq('user_id', user.id),
    ]);
    setEventsCreated(created || 0);
    setEventsAttended(attended || 0);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar forceScrolled />

      {/* Hero */}
      <section className="pt-28 pb-16 px-4 text-center bg-gradient-to-br from-gray-50 to-teal-50">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            {firstName ? `Hey ${firstName}, here's how it works` : 'How It Works'}
          </h1>
          <p className="text-lg text-gray-500 max-w-xl mx-auto">
            Whether you&apos;re attending or organizing, EVENTSHOWCASE makes it simple.
          </p>
          {/* Personalised stats for logged-in users */}
          {user && (eventsCreated > 0 || eventsAttended > 0) && (
            <div className="flex flex-wrap items-center justify-center gap-4 mt-7">
              {eventsCreated > 0 && (
                <div className="bg-white border border-teal-200 rounded-xl px-5 py-3 text-center">
                  <p className="text-2xl font-extrabold text-teal-600">{eventsCreated}</p>
                  <p className="text-xs text-gray-500 mt-0.5">Events created</p>
                </div>
              )}
              {eventsAttended > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl px-5 py-3 text-center">
                  <p className="text-2xl font-extrabold text-gray-900">{eventsAttended}</p>
                  <p className="text-xs text-gray-500 mt-0.5">Events attended</p>
                </div>
              )}
              <Link to="/dashboard" className="bg-teal-600 hover:bg-teal-700 text-white px-5 py-3 rounded-xl text-sm font-semibold transition-colors whitespace-nowrap">
                Go to Dashboard
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* For Attendees */}
      <section className="max-w-5xl mx-auto px-4 py-16">
        <div className="flex items-center gap-3 mb-10">
          <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
            <i className="ri-user-smile-line text-teal-600 text-xl"></i>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">For Attendees</h2>
            <p className="text-sm text-gray-500">Find and attend events you love</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {ATTENDEE_STEPS.map((step, i) => (
            <div key={i} className="bg-white border border-gray-100 rounded-2xl p-6">
              <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center mb-4">
                <i className={`${step.icon} text-teal-600 text-2xl`}></i>
              </div>
              <div className="text-xs font-bold text-teal-500 uppercase tracking-wide mb-2">Step {i + 1}</div>
              <h3 className="text-base font-bold text-gray-900 mb-2">{step.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{step.desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Link to="/discover" className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-xl font-semibold text-sm transition-colors whitespace-nowrap">
            <i className="ri-search-line"></i> Browse Events
          </Link>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4"><div className="border-t border-gray-100"></div></div>

      {/* For Organizers */}
      <section className="max-w-5xl mx-auto px-4 py-16">
        <div className="flex items-center gap-3 mb-10">
          <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
            <i className="ri-calendar-event-line text-gray-700 text-xl"></i>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">For Organizers</h2>
            <p className="text-sm text-gray-500">Create, promote, and earn from your events</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {ORGANIZER_STEPS.map((step, i) => (
            <div key={i} className="bg-gray-50 rounded-2xl p-5">
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center mb-4 border border-gray-100">
                <i className={`${step.icon} text-gray-700 text-xl`}></i>
              </div>
              <div className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">Step {i + 1}</div>
              <h3 className="text-sm font-bold text-gray-900 mb-2">{step.title}</h3>
              <p className="text-xs text-gray-500 leading-relaxed">{step.desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Link to="/signup" className="inline-flex items-center gap-2 bg-gray-900 hover:bg-gray-800 text-white px-6 py-3 rounded-xl font-semibold text-sm transition-colors whitespace-nowrap">
            <i className="ri-add-line"></i> Start for Free
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-gray-50 py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-10">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <FaqItem key={i} question={faq.q} answer={faq.a} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 text-center">
        <div className="max-w-2xl mx-auto">
          {user ? (
            <>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                {eventsCreated === 0 ? 'Ready to create your first event?' : 'Keep the momentum going!'}
              </h2>
              <p className="text-gray-500 mb-8">
                {eventsCreated === 0
                  ? 'You\'ve attended events — now it\'s time to host your own.'
                  : `You've created ${eventsCreated} event${eventsCreated > 1 ? 's' : ''} so far. What's next?`}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to="/create-event" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-8 py-3.5 rounded-xl font-semibold transition-colors whitespace-nowrap">
                  <i className="ri-add-line"></i> Create Event
                </Link>
                <Link to="/dashboard" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border border-gray-200 text-gray-700 hover:bg-gray-50 px-8 py-3.5 rounded-xl font-semibold transition-colors whitespace-nowrap">
                  <i className="ri-dashboard-line"></i> Dashboard
                </Link>
              </div>
            </>
          ) : (
            <>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to get started?</h2>
              <p className="text-gray-500 mb-8">Join thousands of organizers already using EVENTSHOWCASE.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to="/signup" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-8 py-3.5 rounded-xl font-semibold transition-colors whitespace-nowrap">
                  Create Free Account
                </Link>
                <Link to="/discover" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border border-gray-200 text-gray-700 hover:bg-gray-50 px-8 py-3.5 rounded-xl font-semibold transition-colors whitespace-nowrap">
                  Browse Events
                </Link>
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
